import { OPTION_GROUP_LABEL } from '@/lib/pricing-draft'
import { formatWon } from '@/lib/quote'
import type { PricingBody, PricingGroup, PricingItem, PricingPreset } from '@/types/pricing'

/** 저장 전에 "무엇이 어떻게 바뀌는지"를 사람이 읽는 한 줄로 만들어요. */
export interface PricingChange {
  /** '전역' · '프로젝트 유형' · '옵션' · '월 지원' */
  group: string
  text: string
}

type DiffItem = PricingItem & { group?: PricingGroup; page_amount?: number; applies_to?: string[] }

const name = (item: DiffItem) => item.label.trim() || item.key
const groupName = (item: DiffItem) => (item.group ? OPTION_GROUP_LABEL[item.group] : null)

function describeNew(item: DiffItem): string {
  const parts = [groupName(item), formatWon(item.amount)].filter(Boolean)
  return parts.join(' · ')
}

/**
 * 키를 기준으로 짝지어 비교해요. 키 자체를 고친 건 추가+삭제로 보여요 —
 * 이름만 바꾼 건지 다른 항목으로 바꾼 건지 구별할 방법이 없고, 저장 후 결과는 실제로 그거예요.
 */
function diffList(group: string, base: DiffItem[], next: DiffItem[]): PricingChange[] {
  const changes: PricingChange[] = []
  const push = (text: string) => changes.push({ group, text })
  const baseByKey = new Map(base.map((item) => [item.key, item]))
  const nextByKey = new Map(next.map((item) => [item.key, item]))

  for (const item of next) {
    if (!baseByKey.has(item.key)) push(`'${name(item)}' 추가 (${describeNew(item)})`)
  }
  for (const item of base) {
    if (!nextByKey.has(item.key)) push(`'${name(item)}' 삭제 (${formatWon(item.amount)})`)
  }

  for (const item of next) {
    const prev = baseByKey.get(item.key)
    if (!prev) continue
    if (prev.amount !== item.amount) {
      push(`'${name(item)}' 금액 ${formatWon(prev.amount)} → ${formatWon(item.amount)}`)
    }
    // 페이지당 금액은 프로젝트 유형에만 있어요.
    if (prev.page_amount !== item.page_amount && item.page_amount !== undefined) {
      push(`'${name(item)}' 페이지당 금액 ${formatWon(prev.page_amount ?? 0)} → ${formatWon(item.page_amount)}`)
    }
    if (prev.label !== item.label) push(`'${name(prev)}' 라벨 → '${name(item)}'`)
    if (prev.desc !== item.desc) push(`'${name(item)}' 설명 수정`)
    const scope = (list?: string[]) => (list && list.length > 0 ? list.join(', ') : '모든 유형')
    if (scope(prev.applies_to) !== scope(item.applies_to)) {
      push(`'${name(item)}' 붙는 유형 ${scope(prev.applies_to)} → ${scope(item.applies_to)}`)
    }
    if (prev.group !== item.group) {
      push(`'${name(item)}' 그룹 ${groupName(prev)} → ${groupName(item)}`)
    }
  }

  // 추가·삭제를 뺀 나머지의 상대 순서만 비교해요 (추가/삭제 자체는 위에서 이미 알려줬으니까).
  const keptBefore = base.filter((item) => nextByKey.has(item.key)).map((item) => item.key)
  const keptAfter = next.filter((item) => baseByKey.has(item.key)).map((item) => item.key)
  if (keptBefore.join('|') !== keptAfter.join('|')) {
    const order = keptAfter.map((key) => name(nextByKey.get(key) as DiffItem)).join(' · ')
    push(`순서 변경 → ${order}`)
  }

  return changes
}

/**
 * 프리셋은 금액이 아니라 구성이라 한 줄 요약만 해요 — 어떤 옵션이 달라졌는지까지 받아쓰지 않아요.
 * ponytail: 내용 비교는 JSON 문자열. 같은 편집 화면에서 만든 객체라 키 순서가 같아요.
 */
function diffPresets(base: PricingPreset[], next: PricingPreset[]): PricingChange[] {
  const changes: PricingChange[] = []
  const push = (text: string) => changes.push({ group: '프리셋', text })
  const baseByKey = new Map(base.map((p) => [p.key, p]))
  const nextByKey = new Map(next.map((p) => [p.key, p]))
  const label = (preset: PricingPreset) => preset.label.trim() || preset.key

  for (const preset of next) {
    if (!baseByKey.has(preset.key)) push(`'${label(preset)}' 추가`)
  }
  for (const preset of base) {
    if (!nextByKey.has(preset.key)) push(`'${label(preset)}' 삭제`)
  }
  for (const preset of next) {
    const prev = baseByKey.get(preset.key)
    if (prev && JSON.stringify(prev) !== JSON.stringify(preset)) push(`'${label(preset)}' 수정`)
  }

  const keptBefore = base.filter((p) => nextByKey.has(p.key)).map((p) => p.key)
  const keptAfter = next.filter((p) => baseByKey.has(p.key)).map((p) => p.key)
  if (keptBefore.join('|') !== keptAfter.join('|')) {
    const order = keptAfter.map((key) => label(nextByKey.get(key) as PricingPreset)).join(' · ')
    push(`순서 변경 → ${order}`)
  }

  return changes
}

export function diffPricingBody(base: PricingBody, next: PricingBody): PricingChange[] {
  const globals: PricingChange[] = []
  if (base.max_pages !== next.max_pages) {
    globals.push({ group: '전역', text: `페이지 수 상한 ${base.max_pages} → ${next.max_pages}` })
  }
  if (base.online_discount_rate !== next.online_discount_rate) {
    const pct = (rate: number) => `${Math.round(rate * 100)}%`
    globals.push({
      group: '전역',
      text: `온라인 할인율 ${pct(base.online_discount_rate)} → ${pct(next.online_discount_rate)}`,
    })
  }
  if (base.onetime_cap !== next.onetime_cap) {
    globals.push({ group: '전역', text: `구축비 상한 ${formatWon(base.onetime_cap)} → ${formatWon(next.onetime_cap)}` })
  }
  if (base.monthly_free_months !== next.monthly_free_months) {
    globals.push({
      group: '전역',
      text: `월 유지보수 무료 개월 ${base.monthly_free_months} → ${next.monthly_free_months}`,
    })
  }

  return [
    ...globals,
    ...diffList('프로젝트 유형', base.project_types, next.project_types),
    ...diffList('옵션', base.options, next.options),
    ...diffList('월 지원', base.support_tiers, next.support_tiers),
    ...diffPresets(base.presets, next.presets),
  ]
}
